import React, { useState, useEffect, useCallback } from "react";
import { Settings2, RotateCcw, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIM_LABELS = {
  acquisition: "Acquisition",
  previsibilite: "Prévisibilité",
  qualification: "Qualification",
  reactivite: "Réactivité",
  transformation: "Transformation",
  suivi_devis: "Suivi devis",
  digitalisation: "Digitalisation",
};

const Q_LABELS = {
  situation: "Situation entreprise",
  planning_rempli: "Planning rempli",
  previsibilite_3mois: "Prévisibilité 3 mois",
  bouche_a_oreille_pct: "Bouche-à-oreille %",
  source_backup: "Source backup",
  qualite_prospects: "Qualité prospects",
  deplacement_inutile: "Déplacement inutile",
  relance_devis: "Relance devis",
  nombre_relances: "Nombre relances",
  reponse_chantier: "Réponse chantier",
  appels_manques_semaine: "Appels manqués/sem.",
  delai_rappel: "Délai rappel",
  appels_soir_weekend: "Appels soir/weekend",
};

export const ScoringAdminSection = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [source, setSource] = useState("default");

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/config/scoring`);
      setConfig(res.data);
      setSource(res.data.source);
    } catch { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateWeight = (qId, answerId, dim, value) => {
    setConfig((prev) => {
      const rules = { ...prev.rules };
      if (!rules[qId]) rules[qId] = {};
      if (!rules[qId][answerId]) rules[qId][answerId] = {};
      rules[qId][answerId][dim] = parseInt(value) || 0;
      return { ...prev, rules };
    });
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/config/scoring`, {
        rules: config.rules,
        digital_tools: config.digital_tools,
      });
      setSource("custom");
    } catch { /* */ }
    setSaving(false);
  };

  const resetDefaults = async () => {
    if (!window.confirm("Réinitialiser aux valeurs par défaut ?")) return;
    try {
      await axios.post(`${API}/admin/config/scoring/reset`);
      load();
    } catch { /* */ }
  };

  if (loading || !config) {
    return (
      <div className="bg-card rounded-lg border p-6">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><Settings2 className="w-5 h-5 text-primary" /> Scoring</h2>
        <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border p-6" data-testid="scoring-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <Settings2 className="w-5 h-5 text-primary" /> Poids du Scoring
          <span className={`text-xs px-2 py-0.5 rounded ${source === "custom" ? "bg-accent/20 text-accent" : "bg-muted text-muted-foreground"}`}>
            {source === "custom" ? "Personnalisé" : "Par défaut"}
          </span>
        </h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={resetDefaults}><RotateCcw className="w-4 h-4 mr-1" /> Reset</Button>
          <Button size="sm" onClick={saveConfig} disabled={saving}><Save className="w-4 h-4 mr-1" /> {saving ? "..." : "Sauver"}</Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Points ajoutés au score de base (80) pour chaque dimension. Négatif = pénalité. Positif = bonus.</p>

      <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
        {Object.entries(config.rules || {}).map(([qId, answers]) => (
          <div key={qId} className="border rounded-lg p-3">
            <p className="text-sm font-semibold mb-2">{Q_LABELS[qId] || qId}</p>
            <div className="space-y-2">
              {Object.entries(answers).map(([answerId, dims]) => (
                <div key={answerId} className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground w-28 truncate" title={answerId}>{answerId}</span>
                  {Object.entries(dims).map(([dim, pts]) => (
                    <div key={dim} className="flex items-center gap-1">
                      <span className="text-[10px] text-muted-foreground">{DIM_LABELS[dim] || dim}:</span>
                      <input
                        type="number"
                        value={pts}
                        onChange={(e) => updateWeight(qId, answerId, dim, e.target.value)}
                        className="w-14 border rounded px-1.5 py-0.5 text-xs text-center"
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
